export type MarkingCodeStatus =
  | "unknown"
  | "free"
  | "in_circulation"
  | "sold"
  | "transferred"
  | "damaged"

export interface MarkingProductStats {
  total_codes: number
  unknown: number
  free: number
  in_circulation: number
  sold: number
  transferred: number
  damaged: number
}

export interface MarkingProductFolder {
  id: string
  parent_id: string | null
  name: string
  products_count: number
  created_at: string
  updated_at: string
}

export type MarkingAttributeType = "text" | "number" | "select"

export interface MarkingAttributeOption {
  id: string
  label: string
}

export interface MarkingAttribute {
  id: string
  name: string
  value_type: MarkingAttributeType
  position: number
  options: MarkingAttributeOption[]
}

export interface MarkingProductAttributeValue {
  attribute_id: string
  name?: string | null
  value_type?: MarkingAttributeType | null
  value: string
  option_id?: string | null
}

export interface MarkingProduct {
  id: string
  folder_id?: string | null
  gtin: string
  product_group: string
  title: string
  brand?: string | null
  article?: string | null
  tnved?: string | null
  price?: string | null
  /** Этикетка из личной библиотеки для «Печать этикеток». */
  label_document_id?: string | null
  label_document_name?: string | null
  color?: string | null
  size?: string | null
  composition?: string | null
  attribute_values?: MarkingProductAttributeValue[]
  stats: MarkingProductStats
  created_at: string
  updated_at: string
}

export interface MarkingCodeItem {
  id: string
  product_id: string
  gtin: string
  serial_number: string
  has_crypto_tail: boolean
  crypto_tail_len: number
  raw_code: string
  status: MarkingCodeStatus
  print_count: number
  last_printed_at?: string | null
  created_at: string
  product_title?: string | null
  product?: {
    id: string
    gtin: string
    title: string
    product_group: string
    brand?: string | null
    article?: string | null
    tnved?: string | null
    price?: string | null
    color?: string | null
    size?: string | null
    composition?: string | null
    attribute_values?: Array<{ name?: string | null; value?: string | null }>
  } | null
  packs?: Array<{ id: string; seq: number; title: string }>
  kiz?: string
  status_history?: MarkingCodeStatusHistoryItem[]
}

export interface MarkingCodeStatusHistoryItem {
  id: string
  from_status: MarkingCodeStatus
  to_status: MarkingCodeStatus
  comment?: string | null
  user_name?: string | null
  created_at: string
}

export interface MarkingStats {
  active_gtin: number
  total_codes: number
  printed: number
  attention: number
  free: number
}

export interface MarkingPageMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  kpi?: {
    gtin: number
    codes: number
    free: number
  }
}

export interface LabelPrintPayload {
  gtin: string
  product_title: string
  brand?: string
  article?: string
  items: Array<{
    code_id: string
    serial_number: string
    raw_code: string
  }>
  /** Шапка и строки для редактора этикеток. Первая колонка — полный код. */
  matrix?: string[][]
  /** Документ личной библиотеки вместо шаблона «Мои коды» по умолчанию. */
  label_document_id?: string | null
}

export interface MarkingCodePack {
  id: string
  seq: number
  title: string
  comment?: string | null
  /** Код агрегации КИТУ (необязательный, уникальный в компании) */
  kitu_code?: string | null
  source?: "web" | "1c" | "mobile" | string
  external_doc_type?: string | null
  external_doc_number?: string | null
  external_doc_date?: string | null
  verification_status?: "draft" | "in_progress" | "verified" | string
  archived?: boolean
  archived_at?: string | null
  /** Отдавать пакет в device API / мобильные приложения */
  sync_with_mobile?: boolean
  /** Завершён (мобильное «Завершён» / «В работе») */
  finished?: boolean
  finished_at?: string | null
  items_count: number
  verified_count?: number
  created_at: string
  updated_at: string
}

export interface MarkingCodePackItem {
  id: string
  code_id: string
  kiz: string
  has_crypto_tail?: boolean
  gtin: string
  serial_number: string
  raw_code: string
  status: MarkingCodeStatus
  product_title?: string | null
  product_brand?: string | null
  product_article?: string | null
  product_price?: string | null
  product_tnved?: string | null
  product_attributes?: Array<{ name: string; value: string }>
  is_verified?: boolean
  verified_at?: string | null
  created_at: string
}

export interface MarkingPackAttachResult {
  added: number
  duplicates: number
  invalid: number
  not_found: number
  /** Сколько КМ создано в реестре при create_missing */
  created?: number
}

export interface MarkingPackCompareRow extends MarkingCodePackItem {
  pack_titles: string[]
  left_pack_titles?: string[]
  right_pack_titles?: string[]
}

export interface MarkingPackCompareResult {
  summary: {
    left_packs: number
    right_packs: number
    left_unique: number
    right_unique: number
    matched: number
    left_only: number
    right_only: number
  }
  packs: Array<{
    id: string
    title: string
    items_count: number
    archived: boolean
    side: "left" | "right"
  }>
  matched: MarkingPackCompareRow[]
  left_only: MarkingPackCompareRow[]
  right_only: MarkingPackCompareRow[]
}

export const KM_PRINT_QUEUE_KEY = "print_batch_queue"
export const KM_EDITOR_SESSION_KEY = "km_batch_editor"
